import type {NinetailedExperienceEntry} from './contentful-ninetailed'

type RawAudienceEntry = {
  sys: {id: string}
  fields: {nt_name?: string; nt_description?: string}
}

type RawEntry = {
  sys: {id: string; contentType?: {sys: {id: string}}}
  fields: Record<string, unknown>
}

type ContentfulLink = {sys: {type: 'Link'; linkType?: string; id: string}}

export type NinetailedPreviewEntries = {
  experiences: NinetailedExperienceEntry[]
  audiences: RawAudienceEntry[]
}

function isLink(value: unknown): value is ContentfulLink {
  if (!value || typeof value !== 'object') return false
  const sys = (value as {sys?: unknown}).sys
  if (!sys || typeof sys !== 'object') return false
  const linkSys = sys as {type?: unknown; id?: unknown}
  return linkSys.type === 'Link' && typeof linkSys.id === 'string'
}

/**
 * Extracts Ninetailed experience and audience entries from a raw Contentful
 * JSON response. The preview plugin needs these entries to populate its
 * audience/experience selectors.
 *
 * Experience entries in `includes.Entry` reference their audience and variants
 * as Contentful Links (e.g. `{sys: {type: 'Link', id: '...'}}`). The preview
 * plugin runs each experience through `ExperienceMapper.isExperienceEntry`,
 * which strict-validates `nt_audience` and `nt_variants` as full entries — so
 * Links would be rejected and the preview UI would show "No Experiences".
 *
 * We inline those references here using the same `includes.Entry` array as the
 * lookup table so each experience passes validation.
 */
export function extractNinetailedPreviewEntries(rawResponse: unknown): NinetailedPreviewEntries {
  const empty: NinetailedPreviewEntries = {experiences: [], audiences: []}
  if (!rawResponse || typeof rawResponse !== 'object') return empty

  const response = rawResponse as {includes?: {Entry?: unknown[]}}
  const entries = response.includes?.Entry
  if (!Array.isArray(entries)) return empty

  const entryMap = new Map<string, RawEntry>()
  for (const entry of entries) {
    const id = (entry as RawEntry)?.sys?.id
    if (id) entryMap.set(id, entry as RawEntry)
  }

  const resolveLink = (value: unknown): unknown => {
    if (!isLink(value)) return value
    return entryMap.get(value.sys.id) ?? value
  }

  const experiences: NinetailedExperienceEntry[] = []
  const audiences: RawAudienceEntry[] = []
  const seenIds = new Set<string>()

  for (const entry of entries) {
    const e = entry as RawEntry
    const contentTypeId = e?.sys?.contentType?.sys?.id
    const entryId = e?.sys?.id

    if (!contentTypeId || !entryId || seenIds.has(entryId)) continue
    seenIds.add(entryId)

    if (contentTypeId === 'nt_experience') {
      const fields = e.fields ?? {}
      const ntAudience = fields.nt_audience
      const ntVariants = fields.nt_variants

      const resolvedExperience = {
        ...e,
        fields: {
          ...fields,
          nt_audience: ntAudience == null ? ntAudience : resolveLink(ntAudience),
          nt_variants: Array.isArray(ntVariants) ? ntVariants.map(resolveLink) : ntVariants,
        },
      }
      experiences.push(resolvedExperience as NinetailedExperienceEntry)
    } else if (contentTypeId === 'nt_audience') {
      audiences.push(entry as RawAudienceEntry)
    }
  }

  return {experiences, audiences}
}
